import '@silevis/reactgrid/styles.css';

import {
  ReactGrid,
  type Cell,
  type DefaultCellTypes,
  type ReactGridProps,
  type Span,
  type TextCell,
} from '@silevis/reactgrid';
import { type ReactNode } from 'react';

import DeleteIcon from '@/assets/images/trash.png';
import { cn } from '@/lib/utils';

import CurrencyCell from './CurrencyCell';
import HeaderCell from './HeaderCell';
import IconCell from './IconCell';

const EXCEL_GRID = 'c99-excel-grid';
const EXCEL_GRID_HEADER_CELL = 'c99-excel-grid-header-cell';
const EXCEL_GRID_BODY_CELL = 'c99-excel-grid-body-cell';
const EXCEL_GRID_TOTAL_CELL = 'c99-excel-grid-total-cell';
const EXCEL_GRID_CELL_FIXED_LEFT = 'c99-excel-grid-cell--fixed-left';
const EXCEL_GRID_CELL_SORTED = 'c99-excel-grid-cell--sorted';
const EXCEL_GRID_CELL_EOY = 'c99-excel-grid-cell--eoy';
const EXCEL_GRID_CELL_CURRENT_MONTH = 'c99-excel-grid-cell--current-month';
const EXCEL_GRID_BODY_HIGHLIGHTED = 'c99-excel-grid-body-cell--highlighted';
const EXCEL_GRID_BODY_EDITABLE = 'c99-excel-grid-body-cell--editable';
const EXCEL_GRID_BODY_DISABLED = 'c99-excel-grid-body-cell--disabled';
const EXCEL_GRID_TOTAL_NO_RIGHT_BORDER = 'c99-excel-grid-total-cell--no-right-border';

type Props = ReactGridProps & {
  className?: string;
};

export type HeaderCellInterface = Cell &
  Span & {
    type: 'header';
    text: string;
  };

export type CurrencyCellInterface = Cell & {
  type: 'currency';
  text: string;
};

export type IconCellInterface = Cell &
  Span & {
    type: 'icon';
    iconSrc?: string;
    onIconClick?: () => void;
  };

type HeaderCellType = {
  text: string;
  isFixedHeader?: boolean;
  isSorted?: boolean;
  isEoY?: boolean;
  currentMonth?: boolean;
};

type CellType = {
  text: string;
  isFixedLeft?: boolean;
  isSorted?: boolean;
  nonEditable?: boolean;
  disabled?: boolean;
  isEoY?: boolean;
  highlighted?: boolean;
  currentMonth?: boolean;
  renderer?: (text: string) => ReactNode;
};

type TotalCellType = HeaderCellType & {
  isExpanded?: boolean;
};

type IconCellType = {
  disabled?: boolean;
  onIconClick?: () => void;
};

const getCommonCellClassNames = (data: CellType | HeaderCellType | TotalCellType) =>
  cn(
    data.currentMonth && EXCEL_GRID_CELL_CURRENT_MONTH,
    data.isEoY && EXCEL_GRID_CELL_EOY,
    data.isSorted && EXCEL_GRID_CELL_SORTED,
  );

const getBodyCellClassNames = (data: CellType) =>
  cn(
    EXCEL_GRID_BODY_CELL,
    getCommonCellClassNames(data),
    data.isFixedLeft && EXCEL_GRID_CELL_FIXED_LEFT,
    data.highlighted && EXCEL_GRID_BODY_HIGHLIGHTED,
    !data.nonEditable && !data.disabled && EXCEL_GRID_BODY_EDITABLE,
    data.disabled && EXCEL_GRID_BODY_DISABLED,
  );

const getHeaderCellClassNames = (data: HeaderCellType) =>
  cn(
    EXCEL_GRID_HEADER_CELL,
    getCommonCellClassNames(data),
    data.isFixedHeader && EXCEL_GRID_CELL_FIXED_LEFT,
  );

const getTotalCellClassNames = (data: TotalCellType) =>
  cn(
    EXCEL_GRID_BODY_CELL,
    EXCEL_GRID_TOTAL_CELL,
    getCommonCellClassNames(data),
    data.isFixedHeader && EXCEL_GRID_CELL_FIXED_LEFT,
    data.isExpanded && EXCEL_GRID_TOTAL_NO_RIGHT_BORDER,
  );

const getIconCellClassNames = (data: IconCellType) =>
  cn(
    EXCEL_GRID_BODY_CELL,
    !data.onIconClick && EXCEL_GRID_TOTAL_CELL,
    data.disabled && EXCEL_GRID_BODY_DISABLED,
  );

export const getHeaderCell = (data: HeaderCellType): HeaderCellInterface => ({
  ...data,
  type: 'header',
  className: getHeaderCellClassNames(data),
});

export const getTotalCell = (data: TotalCellType): DefaultCellTypes | CurrencyCellInterface => ({
  type: data.isFixedHeader ? 'text' : 'currency',
  className: getTotalCellClassNames(data),
  text: data.text,
  nonEditable: true,
});

export const getIconCell = (data: IconCellType): IconCellInterface => ({
  type: 'icon',
  className: getIconCellClassNames(data),
  iconSrc: data.onIconClick && DeleteIcon,
  onIconClick: data.onIconClick,
});

export const getTextCell = (data: CellType): TextCell => ({
  type: 'text',
  className: getBodyCellClassNames(data),
  nonEditable: data.nonEditable,
  text: data.text,
  renderer: data.renderer,
});

export const getCell = (data: CellType): DefaultCellTypes | CurrencyCellInterface => ({
  type: data.isFixedLeft ? 'text' : 'currency',
  className: getBodyCellClassNames(data),
  nonEditable: data.nonEditable || data.disabled,
  text: data.text,
});

const ExcelGrid = ({ className, ...rest }: Props) => {
  return (
    <div className={cn(EXCEL_GRID, className)}>
      <ReactGrid
        customCellTemplates={{
          currency: new CurrencyCell(),
          header: new HeaderCell(),
          icon: new IconCell(),
        }}
        {...rest}
      />
    </div>
  );
};

export default ExcelGrid;
